import "../surveys/surveys.css";
import "./formOnPdf.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import "pdfjs-dist/build/pdf.worker.entry";
import { answerText, isAnswered } from "../appraisals/appraisalHelpers";
import { QuestionField, SignHere } from "../surveys/surveyAnswerForm";

/**
 * A template built on the company's own form (PDF), shown as the form itself: its pages with
 * every field in its box. Plain elements and its own styles, so it also works on the public
 * link page and inside the app (the /embed pages).
 *
 *   mode "fill" - the person answering (`side`) is taken through their boxes one at a time, in
 *                 the form's order (a prompt to Start - or read the form first - when it opens),
 *                 answering each in a sheet: Back / Next, Skip on an optional one, the signature
 *                 last. They can also tap any of their boxes. The answer then shows in the box.
 *                 The other side's answers (`shown`) are read-only. `signSource` is the
 *                 signature box they sign.
 *   mode "view" - read-only: everyone's answers (`shown`), names, dates and signatures
 *                 (`stampValues`) where they'll be on the finished copy.
 * `onReadToEnd` fires once the last page has been scrolled into view.
 */

// the two sides of a form: the first answers `placement`, the second `placement2` on a "both" question
export const SIDES = { survey: ["respondent", "us"], appraisal: ["officer", "reviewer"], document: ["employee", "employee"] };

export const boxesOf = (template, kind) => {
    const [first, second] = SIDES[kind];
    const boxes = [];
    for (const section of template?.sections || []) {
        for (const q of section.questions || []) {
            if (!q.placement) continue;
            if (q.answeredBy === "both") {
                boxes.push({ key: `${q.id}:1`, q, side: first, placement: q.placement });
                if (q.placement2) boxes.push({ key: `${q.id}:2`, q, side: second, placement: q.placement2 });
            } else boxes.push({ key: q.id, q, side: q.answeredBy, placement: q.placement });
        }
    }
    for (const stamp of template?.stamps || []) if (stamp.placement) boxes.push({ key: `stamp:${stamp.id || stamp.source}`, stamp, placement: stamp.placement });
    return boxes;
};

const reading = (a, b) => a.placement.page - b.placement.page || a.placement.yPercent - b.placement.yPercent || a.placement.xPercent - b.placement.xPercent;

export const FormOnPdf = ({
    pdfUrl, template, kind = "survey", mode = "view", side, answers = {}, onAnswer, shown = {},
    stampValues = {}, signSource = null, signature = null, onSign, onReadToEnd, renderBar, signDrawOnly = false,
}) => {
    const wrap = useRef(null);
    const canvases = useRef({});
    const pageEls = useRef({});
    const endRef = useRef(null);
    const [pages, setPages] = useState([]);
    const [error, setError] = useState(null);
    const [active, setActive] = useState(null);
    const [intro, setIntro] = useState(true);
    const [signedBefore, setSignedBefore] = useState(false); // the signature step opened already signed
    const fill = mode === "fill";
    const boxes = useMemo(() => boxesOf(template, kind), [template, kind]);
    const editable = useMemo(() => (fill ? boxes.filter((b) => b.q && b.side === side).sort(reading) : []), [boxes, fill, side]);
    const answeredCount = editable.filter((b) => isAnswered(b.q, answers[b.q.id])).length;
    // the walk through the form: their boxes in reading order, then the signature
    const signBox = fill && signSource ? boxes.find((b) => b.stamp?.source === signSource) : null;
    const steps = useMemo(() => (signBox ? [...editable, signBox] : editable), [editable, signBox]);
    const isDone = (b) => (b.stamp ? !!signature : isAnswered(b.q, answers[b.q.id]));
    const isRequired = (b) => !!b.stamp || b.q.required !== false;
    const left = steps.filter((b) => isRequired(b) && !isDone(b)).length;
    const started = answeredCount > 0 || !!signature;

    // open the form, then lay out its pages at the width available (pdf.js shows turned pages
    // turned, as the editor did)
    const pdfPages = useRef([]);
    useEffect(() => {
        if (!pdfUrl) return undefined;
        let cancelled = false;
        (async () => {
            try {
                // no OffscreenCanvas: iPhone Safari / the app's web view can drop images with it
                const doc = await pdfjsLib.getDocument({ url: pdfUrl, isOffscreenCanvasSupported: false }).promise;
                const width = Math.max(280, Math.min(wrap.current?.clientWidth || 800, 900));
                const list = [];
                for (let n = 1; n <= doc.numPages; n++) {
                    const page = await doc.getPage(n);
                    const base = page.getViewport({ scale: 1 });
                    list.push({ n, page, scale: width / base.width, width, height: (base.height * width) / base.width });
                }
                if (cancelled) return;
                pdfPages.current = list;
                setPages(list.map(({ n, width: w, height }) => ({ n, width: w, height })));
            } catch (err) {
                if (!cancelled) setError(err.message);
            }
        })();
        return () => { cancelled = true; };
    }, [pdfUrl]);

    // then draw each page once its canvas is on screen (after React has put it there - drawing
    // any earlier left blank pages on slower phones)
    useEffect(() => {
        if (!pages.length) return undefined;
        let cancelled = false;
        (async () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
            for (const { n, page, scale } of pdfPages.current) {
                const canvas = canvases.current[n];
                if (!canvas || cancelled) continue;
                const viewport = page.getViewport({ scale });
                canvas.width = Math.floor(viewport.width * dpr);
                canvas.height = Math.floor(viewport.height * dpr);
                canvas.style.width = `${viewport.width}px`;
                canvas.style.height = `${viewport.height}px`;
                try {
                    await page.render({ canvasContext: canvas.getContext("2d"), viewport, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined }).promise;
                } catch (err) {
                    if (!cancelled) setError(`page ${n}: ${err.message}`);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [pages]);

    // the last page scrolled into view = read to the end
    useEffect(() => {
        if (!onReadToEnd || !pages.length || !endRef.current) return undefined;
        const seen = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { onReadToEnd(); seen.disconnect(); } }, { threshold: 0.1 });
        seen.observe(endRef.current);
        return () => seen.disconnect();
    }, [pages.length]);

    // bring a box into view, near the top (the sheet is at the bottom) - in whatever scrolls the
    // form (the page, or a drawer / modal body)
    const scrollTo = (box) => {
        const pageEl = pageEls.current[box.placement.page];
        if (!pageEl) return;
        let scroller = pageEl.parentElement;
        while (scroller && scroller !== document.body && !(scroller.scrollHeight > scroller.clientHeight + 4 && /(auto|scroll)/.test(getComputedStyle(scroller).overflowY))) scroller = scroller.parentElement;
        const boxTop = pageEl.getBoundingClientRect().top + (pageEl.offsetHeight * box.placement.yPercent) / 100;
        if (scroller && scroller !== document.body) {
            const view = scroller.getBoundingClientRect();
            scroller.scrollTo({ top: Math.max(0, scroller.scrollTop + boxTop - view.top - view.height / 5), behavior: "smooth" });
        } else window.scrollTo({ top: Math.max(0, window.scrollY + boxTop - window.innerHeight / 5), behavior: "smooth" });
    };
    // open a step: the sheet for it, with its box brought into view above the sheet
    const open = (box) => {
        setIntro(false);
        setActive(box);
        setSignedBefore(!!(box?.stamp && signature));
        if (box) scrollTo(box);
    };
    // where to carry on: the first box (in order) not done yet
    const firstOpen = () => steps.find((b) => !isDone(b)) || null;
    const at = active ? steps.findIndex((b) => b.key === active.key) : -1;
    const step = (by) => {
        const to = steps[at + by];
        if (to) open(to); else setActive(null);
    };

    // a rating on their form is just its number - their form says what the numbers mean
    const scale = { max: template?.ratingScale?.max || 5 };
    const boxContent = (b) => {
        if (b.stamp) {
            const value = b.stamp.source === signSource && fill ? signature : stampValues[b.stamp.source];
            if (value?.signature || value?.url) return <img src={value.signature || value.url} alt="" className="fop-sign-img" />;
            if (value?.typedName || value?.text) return <span className="fop-text">{value.typedName || value.text}</span>;
            return b.stamp.source === signSource && fill ? <span className="fop-placeholder">Sign here</span> : null;
        }
        const a = b.side === side && fill ? answers[b.q.id] : shown[b.side]?.[b.q.id];
        if (!a) return b.side === side && fill ? <span className="fop-placeholder">{b.q.required !== false ? "*" : ""}</span> : null;
        if (b.q.type === "checkbox") return a.answer === true ? <span className="fop-tick">✓</span> : null;
        return <span className={`fop-text${b.q.type === "comment" ? " multi" : ""}`}>{answerText(b.q, a, scale)}</span>;
    };
    const boxClass = (b) => {
        if (!fill) return "fop-box view";
        if (b.stamp) return b.stamp.source === signSource ? `fop-box mine sign${signature ? " done" : ""}` : "fop-box view";
        if (b.side !== side) return "fop-box view";
        return `fop-box mine${isAnswered(b.q, answers[b.q.id]) ? " done" : b.q.required !== false ? " required" : ""}${active?.key === b.key ? " active" : ""}`;
    };
    const tap = (b) => {
        if (!fill) return;
        if (b.stamp && b.stamp.source === signSource) return open(b);
        if (b.q && b.side === side) {
            if (b.q.type === "checkbox") { setIntro(false); return onAnswer?.(b.q.id, { answer: answers[b.q.id]?.answer !== true }); }
            open(b);
        }
    };
    const optional = editable.filter((b) => b.q.required === false).length;
    const noun = kind === "document" ? "document" : "form";
    const todo = [
        editable.length && `${editable.length} ${editable.length === 1 ? "box" : "boxes"} to fill in${optional ? ` (${optional} optional)` : ""}`,
        signBox && "your signature",
    ].filter(Boolean).join(", then ");

    return (
        <div className="fop" ref={wrap}>
            {error && <div className="svy-note" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>The form couldn't be shown: {error}</div>}
            {!pages.length && !error && <div className="svy-hint" style={{ padding: "30px 0", textAlign: "center" }}>Opening the form...</div>}
            {pages.map(({ n, width, height }) => (
                <div key={n} className="fop-page" style={{ width, height }} ref={(el) => { pageEls.current[n] = el; }} data-page={n}>
                    <canvas ref={(el) => { canvases.current[n] = el; }} />
                    {boxes.filter((b) => b.placement.page === n).map((b) => {
                        const p = b.placement;
                        const px = (p.heightPercent / 100) * height;
                        return (
                            <div key={b.key} className={boxClass(b)} data-box={b.q ? b.q.text : b.stamp.source} onClick={() => tap(b)}
                                style={{ left: `${p.xPercent}%`, top: `${p.yPercent}%`, width: `${p.widthPercent}%`, height: `${p.heightPercent}%`, fontSize: `${Math.max(6, Math.min(13, px * (b.q?.type === "comment" ? 0.28 : 0.62)))}px` }}>
                                {boxContent(b)}
                            </div>
                        );
                    })}
                </div>
            ))}
            <div ref={endRef} className="fop-end" />

            {fill && renderBar && renderBar({ answered: answeredCount, total: editable.length, left, next: () => open(firstOpen()), nextLabel: started ? "Next field ›" : "Start ›" })}

            {/* when the form opens: what there is to do, and Start (or just read it first) */}
            {fill && intro && !active && pages.length > 0 && steps.length > 0 && firstOpen() && createPortal((
                <div className="fop-sheet-backdrop light" onClick={() => setIntro(false)}>
                    <div className="fop-sheet fop-intro svy-form" data-intro onClick={(e) => e.stopPropagation()}>
                        <div className="fop-intro-title">{started ? `Carry on filling in this ${noun}` : `Fill in this ${noun}`}</div>
                        <div className="fop-intro-text">
                            This {noun} has {todo}. We'll take you through {steps.length === 1 ? "it" : "them one at a time, in order"}{optional ? " - you can skip the optional ones" : ""}.
                        </div>
                        <div className="fop-intro-text muted">The highlighted boxes on the {noun} are yours. You can also tap any of them.</div>
                        <div className="fop-sheet-actions">
                            <button type="button" className="svy-clear" data-read-first onClick={() => setIntro(false)}>Read the {noun} first</button>
                            <button type="button" className="svy-submit" data-start onClick={() => open(firstOpen())}>{started ? "Continue ›" : "Start ›"}</button>
                        </div>
                    </div>
                </div>
            ), document.body)}

            {active && createPortal((
                <div className="fop-sheet-backdrop" onClick={() => setActive(null)}>
                    <div className="fop-sheet svy-form" onClick={(e) => e.stopPropagation()}>
                        <div className="fop-sheet-top">
                            <span className="fop-sheet-count">{at + 1} of {steps.length}{active.stamp ? "" : isRequired(active) ? "" : " · Optional"}</span>
                            <button type="button" className="fop-sheet-close" data-close onClick={() => setActive(null)} aria-label="Close">✕</button>
                        </div>
                        {active.stamp ? (
                            signedBefore && signature ? (
                                <>
                                    <div className="svy-question-text">Your signature</div>
                                    <div className="fop-signed">
                                        {signature.signature ? <img src={signature.signature} alt="Your signature" /> : <span>{signature.typedName}</span>}
                                    </div>
                                    <button type="button" className="svy-clear" onClick={() => { setSignedBefore(false); onSign?.(null); }}>Sign again</button>
                                </>
                            ) : (
                                <>
                                    <div className="svy-question-text">Your signature</div>
                                    <SignHere drawOnly={signDrawOnly} onChange={(value) => onSign?.(value)} />
                                </>
                            )
                        ) : (
                            <>
                                {active.q.type !== "checkbox" && <div className="svy-question-text">{active.q.text}{active.q.required !== false && <span className="svy-required">*</span>}</div>}
                                <QuestionField q={active.q} a={answers[active.q.id] || {}} scale={scale} set={(patch) => onAnswer?.(active.q.id, patch)} />
                            </>
                        )}
                        <div className="fop-sheet-actions">
                            {at > 0 ? <button type="button" className="svy-clear" data-back onClick={() => step(-1)}>‹ Back</button> : <span />}
                            {!isDone(active) && !isRequired(active)
                                ? <button type="button" className="svy-clear fop-skip" data-skip onClick={() => step(1)}>Skip ›</button>
                                : <button type="button" className="svy-submit" data-next disabled={!isDone(active)} onClick={() => step(1)}>
                                    {at < steps.length - 1 ? "Next ›" : "Finish"}
                                </button>}
                        </div>
                    </div>
                </div>
            ), document.body)}
        </div>
    );
};

/**
 * Answering a survey on the company's own form: the form, a bar with progress and Start / "Next field",
 * and Send - which waits until every required box is answered, they've signed (if the form asks),
 * and they've scrolled to the end of the form.
 */
export const SurveyFormFill = ({ form, side = "respondent", submitting = false, onSubmit, submitLabel = "Send my answers" }) => {
    const [answers, setAnswers] = useState({});
    const [signature, setSignature] = useState(null);
    const [readToEnd, setReadToEnd] = useState(false);
    const signSource = form?.signature === "required" ? (side === "us" ? "ourSignature" : "respondentSignature") : null;
    const editable = boxesOf(form?.template, "survey").filter((b) => b.q && b.side === side);
    const missing = editable.filter((b) => b.q.required !== false && !isAnswered(b.q, answers[b.q.id]));
    const blocker = missing.length ? `${missing.length} to answer` : signSource && !signature ? "Sign in the signature box" : !readToEnd ? "Scroll to the end of the form" : null;
    return (
        <FormOnPdf
            pdfUrl={form.formPdfUrl} template={{ ...form.template, stamps: form.stamps || [] }} kind="survey" mode="fill" side={side}
            answers={answers} onAnswer={(id, patch) => setAnswers((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }))}
            stampValues={{ respondentName: side === "respondent" ? { text: form.name } : undefined }}
            signSource={signSource} signature={signature} onSign={setSignature} onReadToEnd={() => setReadToEnd(true)}
            renderBar={({ answered, total, left, next, nextLabel }) => (
                <div className="fop-bar">
                    <span className="fop-bar-count">{answered} of {total} answered</span>
                    {left > 0 && <button type="button" className="svy-clear" data-next-field onClick={next}>{nextLabel}</button>}
                    <button type="button" className="svy-submit" disabled={submitting || !!blocker} onClick={() => onSubmit(answers, signSource ? signature : undefined)}>
                        {submitting ? "Sending..." : blocker || submitLabel}
                    </button>
                </div>
            )}
        />
    );
};

// a survey that's on the company's own form (placed fields) - answered on the form itself
export const onForm = (form) => !!form?.formPdfUrl && (form.template?.sections || []).some((s) => s.questions.some((q) => q.placement));
